import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import queryString from 'query-string';
import moment from 'moment';
import { FormattedMessage as FM } from 'react-intl';

import { PageLayout } from 'common/components';
import TradingDateRangeField from 'common/components/form-fields/TradingDateRangeField/TradingDateRangeField';

import { PartnershipUsersTable, ClientsBalanceTab, PartnershipIBTreeTable } from '../../_components';

import { getPartnershipClientsBalance, getPartnershipIBTree } from '../../_redux';
import { getSymbolGroups as fetchSymbolGroupsApi } from '../../api';

// ─── Simple SymbolGroup multi-select dropdown ────────────────────────────────
const SymbolGroupSelect = ({ options, selected, onChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = e => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedNames = options
    .filter(o => selected.includes(String(o.id)))
    .map(o => o.name)
    .join(', ');

  return (
    <div className="PartnershipClientsPage__SymbolGroupSelect" ref={ref}>
      <button type="button" className="PartnershipClientsPage__SymbolGroupBtn" onClick={() => setIsOpen(v => !v)}>
        <span>{selectedNames || 'All'}</span>
        <span className="PartnershipClientsPage__Arrow">▾</span>
      </button>
      {isOpen && (
        <div className="PartnershipClientsPage__SymbolGroupDropdown">
          {options.length === 0 && (
            <div className="PartnershipClientsPage__SymbolGroupEmpty">
              <FM id="noItems" defaultMessage="No items" />
            </div>
          )}
          {options.map(opt => {
            const id = String(opt.id);
            const checked = selected.includes(id);
            const checkboxCls = checked
              ? 'PartnershipClientsPage__Checkbox PartnershipClientsPage__Checkbox--checked'
              : 'PartnershipClientsPage__Checkbox';
            return (
              <label key={opt.id} className="PartnershipClientsPage__SymbolGroupOption">
                <input
                  type="checkbox"
                  className="PartnershipClientsPage__CheckboxInput"
                  checked={checked}
                  onChange={() => {
                    if (checked) onChange(selected.filter(s => s !== id));
                    else onChange([...selected, id]);
                  }}
                />
                <span className={checkboxCls}>
                  {checked && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4L3.5 6.5L9 1"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span className="PartnershipClientsPage__OptionText">{opt.name}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

SymbolGroupSelect.propTypes = {
  options: PropTypes.array,
  selected: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

SymbolGroupSelect.defaultProps = {
  options: [],
  selected: [],
};

// ─── Main page component ──────────────────────────────────────────────────────
const PartnershipClientsPage = ({
  getPartnershipClientsBalance,
  partnershipClientsBalance,
  partnershipClients,
  partnershipClientsAreLoaded,
  getPartnershipIBTree,
  partnershipIBTree,
  partnershipIBTreeIsLoaded,
  viewEmailPermission,
  viewPhonePermission,
  viewSubIbTreePermission,
  location,
  history,
}) => {
  const defaultTradingDate = useMemo(
    () => `${moment().subtract(7, 'days').format('YYYY-MM-DD')}_${moment().format('YYYY-MM-DD')}`,
    []
  );

  // URL query
  const query = useMemo(() => {
    const parsed = queryString.parse(location.search);
    return { page_size: 10, ...parsed };
  }, [location.search]);

  // ── Ensure trading_date is always present in URL ──
  useEffect(() => {
    if (!query.trading_date) {
      const parsed = queryString.parse(location.search);
      const next = queryString.stringify({ ...parsed, trading_date: defaultTradingDate, page: 1, page_size: 10 });
      history.replace({ search: next });
    }
  }, []);

  // ── Local filter state ──
  const [tradingDate, setTradingDate] = useState(query.trading_date || defaultTradingDate);
  const selectedSymbolGroups = useMemo(() => {
    const raw = query.symbol_group;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw];
  }, [query.symbol_group]);
  const [symbolGroupOptions, setSymbolGroupOptions] = useState([]);

  // ── Load symbol group options on mount ──
  useEffect(() => {
    fetchSymbolGroupsApi()
      .then(res => {
        if (res && res.data) {
          const data = Array.isArray(res.data) ? res.data : res.data.results || [];
          setSymbolGroupOptions(data);
        }
      })
      .catch(() => {});
  }, []);

  // ── Fetch IB tree whenever query changes ──
  const lastQueryKey = useRef(null);
  useEffect(() => {
    if (!query.trading_date) return; // wait for URL to be initialized with trading_date
    const finalQuery = {
      ...query,
      trading_date: query.trading_date,
    };
    const queryKey = JSON.stringify(finalQuery);
    if (lastQueryKey.current === queryKey) return;
    lastQueryKey.current = queryKey;
    getPartnershipIBTree(finalQuery);
  }, [getPartnershipIBTree, query]);

  // ── Also fetch balance (keep existing behaviour) ──
  // getPartnershipClients 已废弃，改用 getPartnershipIBTree
  const getList = useCallback(
    q => getPartnershipIBTree({ ...q, trading_date: query.trading_date }),
    [getPartnershipIBTree, query]
  );
  useEffect(() => {
    getPartnershipClientsBalance();
  }, []);
  // useEffect(() => {
  //   getList({ search: query });
  // }, [getList, query]);
  // ── URL helpers (strip pagination before pushing) ──
  const pushQuery = useCallback(
    updates => {
      const { page_size: ps, page: pg, ...rest } = queryString.parse(location.search);
      const next = queryString.stringify({
        ...rest,
        ...updates,
        page: 1,
        page_size: 10,
      });
      history.replace({ search: next });
    },
    [location.search, history]
  );

  // ── Search state ──
  const [searchValue, setSearchValue] = useState(query.search || '');
  const handleSearchKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        pushQuery({ search: searchValue || undefined });
      }
    },
    [pushQuery, searchValue]
  );

  const handleTradingDateChange = useCallback(
    dateRange => {
      setTradingDate(dateRange);
      pushQuery({ trading_date: dateRange || undefined });
    },
    [pushQuery]
  );

  const handleSymbolGroupChange = useCallback(
    groups => {
      pushQuery({
        symbol_group: groups.length > 0 ? groups : undefined,
      });
    },
    [pushQuery]
  );

  return (
    <PageLayout captionSlug="partnershipClients">
      {/* ── Filter bar ── */}
      <div className="PartnershipClientsPage__Filters">
        <div className="PartnershipClientsPage__FilterItem">
          <span className="PartnershipClientsPage__FilterLabel">Close Trade</span>
          <TradingDateRangeField value={tradingDate} onChange={handleTradingDateChange} />
        </div>
        <div className="PartnershipClientsPage__FilterItem">
          <span className="PartnershipClientsPage__FilterLabel">Symbol Group</span>
          <SymbolGroupSelect
            options={symbolGroupOptions}
            selected={selectedSymbolGroups}
            onChange={handleSymbolGroupChange}
          />
        </div>
        <div className="PartnershipClientsPage__FilterItem">
          <span className="PartnershipClientsPage__FilterLabel">Search</span>
          <input
            type="text"
            className="PartnershipClientsPage__SearchInput"
            placeholder="Name / Email"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>
      </div>

      {/* ── Balance tab ── */}
      <ClientsBalanceTab partnershipClientsBalance={partnershipClientsBalance} />

      {/* ── IB Tree table ── */}
      <PartnershipIBTreeTable
        ibTree={partnershipIBTree}
        ibTreeIsLoaded={partnershipIBTreeIsLoaded}
        location={location}
        history={history}
      />

      {/* ── Keep original table available (hidden but data loaded) ── */}
      <div style={{ display: 'none' }}>
        <PartnershipUsersTable
          data={partnershipClients}
          getList={getList}
          loading={!partnershipClientsAreLoaded}
          location={location}
          history={history}
          params={{ search: query }}
          viewEmailPermission={viewEmailPermission}
          viewPhonePermission={viewPhonePermission}
          viewSubIbTreePermission={viewSubIbTreePermission}
        />
      </div>

      <style jsx>{`
        .PartnershipClientsPage__Filters {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          align-items: flex-end;
          margin-bottom: 20px;
        }
        .PartnershipClientsPage__FilterItem {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .PartnershipClientsPage__FilterItem--search {
          align-self: flex-end;
        }
        .PartnershipClientsPage__FilterLabel {
          font-size: 12px;
          font-weight: 500;
          color: #555;
          letter-spacing: 0.2px;
        }
        :global(.DateRangeField__picker) {
          height: 38px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          background: #fff;
          font-size: 13px;
          color: #333;
          padding: 0 12px;
          cursor: pointer;
          transition: border-color 0.15s;
        }
        :global(.DateRangeField__picker:hover) {
          border-color: #aaa;
        }
        :global(.DateRangeField__picker svg) {
          top: 50% !important;
          transform: translateY(-50%) !important;
        }
        .PartnershipClientsPage__SearchInput {
          height: 38px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          background: #fff;
          font-size: 13px;
          color: #333;
          padding: 0 12px;
          min-width: 230px;
          transition: border-color 0.15s;
        }
        .PartnershipClientsPage__SearchInput:hover {
          border-color: #aaa;
        }
        .PartnershipClientsPage__SearchInput:focus {
          border-color: #2fa7dc;
          outline: none;
        }
        :global(.PartnershipClientsPage__SymbolGroupSelect) {
          position: relative;
        }
        :global(.PartnershipClientsPage__SymbolGroupBtn) {
          height: 38px;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          background: #fff;
          padding: 0 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 13px;
          color: #333;
          min-width: 230px;
          justify-content: space-between;
          transition: border-color 0.15s;
        }
        :global(.PartnershipClientsPage__SymbolGroupBtn:hover) {
          border-color: #aaa;
        }
        :global(.PartnershipClientsPage__Arrow) {
          font-size: 10px;
          color: #999;
        }
        :global(.PartnershipClientsPage__SymbolGroupDropdown) {
          position: absolute;
          top: 100%;
          left: 0;
          z-index: 100;
          background: #fff;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          min-width: 200px;
          max-height: 260px;
          overflow-y: auto;
          padding: 6px 0;
          margin-top: 4px;
        }
        :global(.PartnershipClientsPage__SymbolGroupOption) {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 13px;
          gap: 8px;
        }
        :global(.PartnershipClientsPage__SymbolGroupOption:hover) {
          background: #f5f5f5;
        }
        :global(.PartnershipClientsPage__CheckboxInput) {
          display: none;
        }
        :global(.PartnershipClientsPage__OptionText) {
          flex: 1;
          color: #333;
          line-height: 1.4;
        }
        :global(.PartnershipClientsPage__Checkbox) {
          width: 16px;
          height: 16px;
          border-radius: 3px;
          border: 1.5px solid #ccc;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.15s;
        }
        :global(.PartnershipClientsPage__Checkbox--checked) {
          background: #2fa7dc;
          border-color: #2fa7dc;
        }
        :global(.PartnershipClientsPage__SymbolGroupEmpty) {
          padding: 12px;
          color: #999;
          font-size: 13px;
          text-align: center;
        }
      `}</style>
    </PageLayout>
  );
};

PartnershipClientsPage.propTypes = {
  getPartnershipClientsBalance: PropTypes.func.isRequired,
  getPartnershipIBTree: PropTypes.func.isRequired,
  partnershipClientsBalance: PropTypes.object,
  partnershipClients: PropTypes.object.isRequired,
  partnershipClientsAreLoaded: PropTypes.bool.isRequired,
  partnershipIBTree: PropTypes.object,
  partnershipIBTreeIsLoaded: PropTypes.bool.isRequired,
  viewEmailPermission: PropTypes.bool.isRequired,
  viewPhonePermission: PropTypes.bool.isRequired,
  viewSubIbTreePermission: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

PartnershipClientsPage.defaultProps = {
  partnershipClientsBalance: null,
  partnershipIBTree: { results: [], count: 0 },
};

export default connect(
  state => ({
    partnershipClientsAreLoaded: state.client.partnership.partnershipClientsAreLoaded,
    partnershipClientsBalance: state.client.partnership.partnershipClientsBalance,
    partnershipClients: state.client.partnership.partnershipClients,
    partnershipIBTree: state.client.partnership.partnershipIBTree,
    partnershipIBTreeIsLoaded: state.client.partnership.partnershipIBTreeIsLoaded,
    viewDetailsPermission: state.interfaceConfig.partnership.viewDetailsPermission,
    viewEmailPermission: state.interfaceConfig.partnership.viewEmailPermission,
    viewPhonePermission: state.interfaceConfig.partnership.viewPhonePermission,
    viewSubIbTreePermission: state.interfaceConfig.partnership.viewSubIbTreePermission,
  }),
  {
    getPartnershipClientsBalance: () => getPartnershipClientsBalance.request(),
    getPartnershipIBTree: params => getPartnershipIBTree.request(params),
  }
)(PartnershipClientsPage);
export { PartnershipClientsPage };
